<script lang="ts">
	import { addComment, getCommentsAll, deleteComment } from '$lib/components/comments/comments.api';
	import { updateCommentsForPost } from '$lib/components/comments/comment.store';
	import type { CommentAttrsArray } from '$lib/components/comments/comment.type';
	import { onMount } from 'svelte';
	import { supabaseClient } from '$lib/supabase';
	import { handleAlert } from '../alert';
	import { v4 as uuidv4 } from 'uuid';
	import { user, getUsernamesAll } from '$lib/user';
	import { browser } from '$app/environment';
	// import { goto } from '$app/navigation';
	import { addNotification } from '$lib/chat/chat.api';
	import DOMPurify from 'dompurify';
	import { writable } from 'svelte/store';

	let comments: CommentAttrsArray = [];
	export let post_id: string;
	export let replied_to_username: string;
	export let post_slug: string;
	export let username: string | undefined;
	export let id;
	let showSuggestions = false;
	let filteredUsernames: string[] = [];
	let input: HTMLTextAreaElement;
	let created_at = new Date();
	let replied_to_id: '';

	// Store to hold member usernames
	export const memberUsernames = writable([]);

	let newComment = {
		id: uuidv4(),
		date: new Date(),
		message: '',
		post_id: '',
		replied_to_username: '',
		author_username: username,
		created_by: id,
		replied_to_id: ''
	};

	let loading = true;
	// let replyToCommentId = null;

	// *** at mention functions ***

	const filterUsernames = () => {
		const inputText = newComment.message.slice(newComment.message.lastIndexOf('@') + 1);
		filteredUsernames = $memberUsernames.filter((username) =>
			username.toLowerCase().startsWith(inputText.toLowerCase())
		);
		showSuggestions = filteredUsernames.length > 0;
	};

	$: {
		// Check if the user is typing '@'
		filterUsernames();
	}

	const handleClickOutside = (event) => {
		if (
			!event.target.closest('.suggestions-list') &&
			!event.target.closest('.suggestions-dropdown')
		) {
			showSuggestions = false;
		}
	};

	// *** end at mention functions ***

	onMount(async () => {
		try {
			// Add event listener to handle clicks outside suggestions
			document.body.addEventListener('click', handleClickOutside);

			// Subscribe to real-time changes
			const memberComments = supabaseClient
				.channel('schema-db-changes')
				.on(
					'postgres_changes',
					{
						event: 'INSERT',
						schema: 'public',
						table: 'comments'
					},
					(payload) => {
						if (payload.new.post_id === post_id) {
							updateCommentsForPost(post_id, [...comments, payload.new]);
						}
					}
				)
				.subscribe();

			// Fetch usernames
			const response = await getUsernamesAll();
			if (response && response.data && Array.isArray(response.data)) {
				const usernames = response.data.map((profile) => profile.username);
				memberUsernames.set(usernames);
			} else {
				console.error('Invalid data format:', response);
			}

			await loadCommentsAndUpdateLoadingState();

			return () => {
				memberComments.unsubscribe();
				document.body.removeEventListener('click', handleClickOutside);
			};
		} catch (error) {
			handleAlert({ type: 'error', text: error.message });
		}
	});

	// Reactively load comments whenever post_id changes
	$: loadCommentsAndUpdateLoadingState();

	// beforeUpdate(() => {
	// 	loading = true;
	// 	comments = [];
	// });

	// afterUpdate(async () => {
	// 	if (post_id) {
	// 		comments = [];
	// 		loading = true;
	// 		await loadComments();
	// 		loading = false;
	// 	}
	// });

	// 		await loadComments();
	// 		// console.log('loadComments ran');
	// 		const memberComments = supabaseClient
	// 			.channel('schema-db-changes')
	// 			.on(
	// 				'postgres_changes',
	// 				{
	// 					event: 'INSERT',
	// 					schema: 'public',
	// 					table: 'comments'
	// 				},
	// 				(payload) => {
	// 					comments = [...comments, payload.new];
	// 				}
	// 			)
	// 			.subscribe();

	// 		loading = false;

	// 		// function for at mentions
	// 		try {
	// 			const response = await getUsernamesAll();

	// 			if (response && response.data && Array.isArray(response.data)) {
	// 				const usernames = response.data.map((profile) => profile.username);
	// 				memberUsernames.set(usernames);
	// 			} else {
	// 				console.error('Invalid data format:', response);
	// 			}
	// 		} catch (error) {
	// 			console.error('Error fetching usernames:', error);
	// 		}
	// 		// Add event listener to handle clicks outside suggestions
	// 		document.body.addEventListener('click', handleClickOutside);

	// 		return () => memberComments.unsubscribe();
	// 	} catch (error) {
	// 		handleAlert({ type: 'error', text: error.message });
	// 	}
	// });

	async function loadCommentsAndUpdateLoadingState() {
		if (post_id) {
			loading = true; // Set loading to true before loading comments
			comments = []; // Clear comments before loading new ones
			newComment.post_id = post_id;
			await loadComments(); // Load comments asynchronously
			loading = false; // Set loading to false after loading comments
		}
	}

	async function loadComments() {
		try {
			const result = await getCommentsAll(post_id);
			if (result.data) {
				comments = result.data;
				updateCommentsForPost(post_id, comments);
			}
		} catch (error) {
			handleAlert({ type: 'error', text: error.message });
		}
	}

	async function handleSubmit() {
		if (browser && (!$user || !$user.member)) {
			handleAlert({
				type: 'default',
				text: 'Join as a member to comment.'
			});
			return;
			// goto('/auth');
		}
		// if (newComment.message.trim() === '') return;

		const CLEAN_MESSAGE = DOMPurify.sanitize(newComment.message);

		await addComment(
			newComment.id,
			newComment.date,
			CLEAN_MESSAGE,
			newComment.post_id,
			newComment.replied_to_username,
			newComment.author_username,
			newComment.created_by,
			replied_to_id
			// handleNotifications(CLEAN_MESSAGE)
		);

		let type = 'reply';
		let medium = 'post';

		// TODO this might need modification so the user can be dropped into the comment id in the thread on the post
		await addNotification(
			CLEAN_MESSAGE,
			username,
			handleNotifications(CLEAN_MESSAGE),
			replied_to_username,
			false,
			post_id,
			newComment.author_username,
			created_at,
			type,
			medium,
			post_slug
		);

		newComment = {
			id: uuidv4(),
			date: new Date(),
			message: '',
			post_id: post_id,
			replied_to_username: replied_to_username,
			author_username: username,
			created_by: id,
			replied_to_id: ''
		};

		// Fetch comments again after adding a new comment
		await loadComments();
	}

	function handleInput(event) {
		newComment.message = event.target.value;
	}

	async function deleteMemberComment(commentId: string) {
		try {
			await deleteComment(commentId);
			comments = comments.filter((comment) => comment.id !== commentId);
		} catch (error) {
			handleAlert({ type: 'error', text: error.message });
		}
	}

	// function replyToComment(author_username: string, commentId: string) {
	// 	newComment.replied_to_username = author_username;
	// 	replyToCommentId = commentId;
	// 	if (input) {
	// 		input.focus();
	// 	}
	// }

	const handleNotifications = (CLEANED_MESSAGE: string) => {
		const mentions = CLEANED_MESSAGE.match(/@(\w+)/g);
		return mentions ? mentions.map((mention) => mention.slice(1)) : [];
	};

	// const parseMentionsHyperlinksAndDomains = (text) => {
	// 	// Regular expression to match mentions
	// 	const mentionRegex = /@([a-zA-Z0-9_]+)/g;

	// 	// Replace mentions with links to member profiles
	// 	const parsedMentions = text.replace(mentionRegex, (match, author_username) => {
	// 		return `<a href="/member/${author_username}/profile" rel="noopener noreferrer">${match}</a>`;
	// 	});

	// 	// Regular expression to match hyperlinks and domain names
	// 	const linkRegex = /(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/g;

	// 	// Replace hyperlinks and domain names with anchor tags
	// 	const parsedHyperlinksAndDomains = parsedMentions.replace(linkRegex, (match, url) => {
	// 		const fullUrl = url.startsWith('http') ? url : `https://${url}`;
	// 		return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${url}</a>`;
	// 	});

	// 	return parsedHyperlinksAndDomains;
	// };

	// const parsedMessage = parseMentionsHyperlinksAndDomains(newComment.message);
</script>

{#if post_id}
	<div class="font-exo-2 relative">
		{#if $user?.member}
			<h2 class="text-3xl my-6 gray-200">Add a Comment</h2>
		{/if}

		<form on:submit|preventDefault={handleSubmit}>
			{#if $user?.member}
				<label>
					<textarea
						class="bg-cosmicPurple800 w-full 2xl:w-1/2 border-cosmicMagenta400 text-gray-200"
						bind:this={input}
						bind:value={newComment.message}
						on:input={() => {
							filterUsernames();
							handleInput(event);
						}}
						required
						placeholder={newComment.replied_to_username
							? `Replying to ${newComment.replied_to_username}`
							: ''}
					></textarea>
					{#if showSuggestions && filteredUsernames.length > 0}
						<div class="absolute left-0 bg-cosmicPurple800 w-64 rounded-md p-4">
							{#each filteredUsernames as username}
								<div
									class="suggestion"
									on:click={() => {
										// Replace text with the selected suggestion
										const atIndex = newComment.message.lastIndexOf('@');
										newComment.message = newComment.message.slice(0, atIndex + 1) + username + ' ';
										// Close suggestions box
										showSuggestions = false;
										// Set focus back to the textarea
										input.focus();
									}}
									on:keyup={() => {
										// Replace text with the selected suggestion
										const atIndex = newComment.message.lastIndexOf('@');
										newComment.message = newComment.message.slice(0, atIndex + 1) + username + ' ';
										showSuggestions = false;
										// Set focus back to the textarea
										input.focus();
									}}
									tabindex="0"
									role="button"
								>
									{username}
								</div>
							{/each}
						</div>
					{/if}
				</label><br />
			{/if}
			<button class=" rounded-full border-cosmicPurple100 text-cosmicPurple100 mb-4" type="submit"
				>Add Comment</button
			>
		</form>

		{#if loading}
			<p>Loading comments...</p>
		{:else}
			<div class="px-0 sm:px-4 mx-auto mt-4 xl:w-11/12 2xl:w-5/6 3xl:w-3/4 4xl:w-2/3">
				<div class="px-3 md:px-10 lg:px-5 xl:px-10">
					{#each comments.slice().reverse() as comment}
						<div class="border-t border-cosmicMagenta400 mx-auto">
							<div class="my-4 text-left max-w-[800px]">
								<div class="text-sm">
									<a class="text-cosmicPurple100" href="/member/{comment.author_username}/profile"
										>{comment.author_username}</a
									>
								</div>
								<div class="my-2 whitespace-pre-line text-gray-200">
									{comment.message}
									<!-- <SafeHtml html={parseMentionsHyperlinksAndDomains(comment.message)} /> -->
								</div>
								{#if id === comment.created_by}
									<button
										class="rounded-lg border-cosmicPurple100 text-cosmicPurple100 text-xs cursor-pointer hover:bg-cosmicPurple100 hover:text-cosmicPurplebg transition-colors duration-200 font-semibold mr-4"
										on:click={() => deleteMemberComment(comment.id)}>Delete</button
									>
								{/if}
								<!-- <button
							class="rounded-lg border-cosmicPurple100 text-cosmicPurple100 text-xs cursor-pointer hover:bg-cosmicPurple100 hover:text-cosmicPurplebg transition-colors duration-200 font-semibold"
							on:click={() => replyToComment(comment.author_username, comment.id)}>Reply</button
						> -->
								<!-- Reply input field -->
								<!-- {#if replyToCommentId === comment.id}
							<form on:submit|preventDefault={handleSubmit}>
								<label>
									<textarea
										class="bg-cosmicPurple800 w-full 2xl:w-1/2 border-cosmicMagenta400 mt-2"
										bind:this={input}
										bind:value={newComment.message}
										on:input={() => {
											filterUsernames();
											handleInput(event);
										}}
										required
										placeholder={`Replying to ${comment.author_username}`}
									></textarea>
									{#if showSuggestions && filteredUsernames.length > 0}
										<div class="absolute left-0 bg-cosmicPurple800 w-64 rounded-md p-4">
											{#each filteredUsernames as username}
												<div
													class="suggestion"
													on:click={() => {
														const atIndex = newComment.message.lastIndexOf('@');
														newComment.message =
															newComment.message.slice(0, atIndex + 1) + username + ' ';
														showSuggestions = false;
														input.focus();
													}}
													on:keydown={(event) => {
														if (event.key === 'Enter') {
															const atIndex = newComment.message.lastIndexOf('@');
															newComment.message =
																newComment.message.slice(0, atIndex + 1) + username + ' ';
															showSuggestions = false;
															input.focus();
														}
													}}
													tabindex="0"
													role="button"
												>
													{username}
												</div>
											{/each}
										</div>
									{/if}
								</label><br />
								<button
									class="rounded-full border-cosmicPurple100 text-cosmicPurple100 mb-4"
									type="submit">Reply</button
								>
							</form>
						{/if} -->
							</div>
						</div>
						<!-- Display the replied-to comment underneath -->
						<!-- {#if comment.replied_to_id}
					{#each comments as repliedComment}
						{#if repliedComment.id === comment.replied_to_id}
							<div class="my-4 ml-4">
								<div class="text-sm">
									<a
										class="text-cosmicPurple100"
										href="/member/{repliedComment.author_username}/profile"
									>
										{repliedComment.author_username}
									</a>
								</div>
								<div class="my-2 whitespace-pre-line">{repliedComment.message}</div>
							</div>
						{/if}
					{/each}
				{/if} -->
					{/each}
				</div>
			</div>
		{/if}
	</div>
{/if}
